import { Injectable } from '@angular/core';
import {
  PersonalSubscriptionType,
  SubscriptionDefinitionDto,
  SubscriptionDto,
  SubscriptionType,
} from '../../../api/portal-api.generated';
import { ProfileDto } from '../../../api/wallet-api.generated';
import { SubscriptionCard } from '../../components/subscription-card/models/subscription-card.model';
import { SubscriptionStatus } from '../../components/subscription-card/models/subscription-status.enum';
import { DateUtils } from '../../../utils/date.utils';
import { BASICBLUE, BASICWHITE, MONTH_IN_MILISECONDS, PREMIUMBLUE, PREMIUMWHITE } from '../../constants/value';
import { EnterpriseSubscriptionType } from '../../components/subscription-card/models/enterprise-subscription-type.enum';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionsService {
  private activeSubscriptions: SubscriptionDto[];

  private expiredSubscriptions: SubscriptionDto[];

  private offeredSubscriptions: Record<string, SubscriptionDefinitionDto[]> = {};

  get activeSubscriptionsList() {
    return this.activeSubscriptions;
  }

  setActiveSubscriptions(subs: SubscriptionDto[]) {
    this.activeSubscriptions = this.mergeSubscriptions(subs);
  }

  getActiveSubscriptions(profileId: string): SubscriptionDto[] | null {
    return this.activeSubscriptions?.some((sub) => sub.profileId === profileId) ? this.activeSubscriptions : null;
  }

  setOfferedSubscriptions(subs: SubscriptionDefinitionDto[], profileId: string) {
    this.offeredSubscriptions[profileId] = subs;
  }

  getOfferedSubscriptions(profileId: string): SubscriptionDefinitionDto[] | null {
    return this.offeredSubscriptions[profileId] ?? [];
  }

  setExpiredSubscriptions(subs: SubscriptionDto[]) {
    this.expiredSubscriptions = subs;
  }

  getExpiredSubscriptions(profileId: string): SubscriptionDto[] | null {
    return this.expiredSubscriptions?.some((sub) => sub.profileId === profileId) ? this.expiredSubscriptions : null;
  }

  isCardMatchingDefinition(card: Record<string, any>, subscriptionDefinition: SubscriptionDefinitionDto) {
    if (card.type === 'Business') {
      return card.type === subscriptionDefinition.subscriptionType && card.title === subscriptionDefinition.name;
    } else if (card.type === 'Personal') {
      return (
        card.type === subscriptionDefinition.subscriptionType &&
        card.subType === subscriptionDefinition.personalSubscriptionType
      );
    }
    return card.type === subscriptionDefinition.subscriptionType;
  }

  mapActiveSubscriptions(cards: Record<string, any>[], profile: ProfileDto): Record<string, any>[] {
    const offeredSubs = this.offeredSubscriptions[profile?.id];
    const activeSubs = this.activeSubscriptions;
    const filteredCards = cards?.filter(
      (card) =>
        offeredSubs?.some((subDefinition) => this.isCardMatchingDefinition(card, subDefinition)) ||
        activeSubs?.some((sub) => this.isCardMatchingDefinition(card, sub.definition))
    );

    filteredCards?.forEach((card) => {
      let targetActiveSub: any = {};
      let targetOfferedSub: any = {};
      let targetExpiredSub: any = {};

      if (card.type === SubscriptionType.Business) {
        targetActiveSub = this.activeSubscriptions?.find(
          (sub) => card.type === sub.definition.subscriptionType && card.title === sub.definition.name
        );

        targetOfferedSub = offeredSubs?.find((sub) => card.type === sub.subscriptionType && card.title === sub.name);

        targetExpiredSub = this.expiredSubscriptions?.find(
          (sub) => card.type === sub.definition.subscriptionType && card.title === sub.definition.name
        );
      } else {
        targetActiveSub = this.activeSubscriptions?.find(
          (sub) =>
            card.type === sub.definition.subscriptionType && card.subType === sub.definition.personalSubscriptionType
        );

        targetOfferedSub = offeredSubs?.find(
          (sub) => card.type === sub.subscriptionType && card.subType === sub.personalSubscriptionType
        );

        targetExpiredSub = this.expiredSubscriptions?.find(
          (sub) =>
            card.type === sub.definition.subscriptionType && card.subType === sub.definition.personalSubscriptionType
        );
      }

      if (targetExpiredSub) {
        card.startedAt = targetExpiredSub.startedAt;

        const endsDate = new Date(targetExpiredSub.endsAt).setUTCHours(0, 0, 0, 0);
        card.endsAt = new Date(endsDate).toISOString();

        if (card.type === SubscriptionType.Personal && card.subType === PersonalSubscriptionType.Gold) {
          card.active = true;
        } else {
          card.active = targetExpiredSub.activated;
        }
      }

      if (targetActiveSub) {
        card.startedAt = targetActiveSub.startedAt;

        const endsDate = new Date(targetActiveSub.endsAt).setUTCHours(0, 0, 0, 0);
        card.endsAt = new Date(endsDate).toISOString();

        card.active = targetActiveSub.activated;

        if (new Date(card.startedAt) > new Date()) {
          card.notStarted = true;
        }
      }

      if (targetOfferedSub) {
        card.price = targetOfferedSub.priceNetto;
        card.name = targetOfferedSub.name;
        card.inactivePrice = this.getSubscriptionPrice(targetOfferedSub);
        card.id = targetOfferedSub.id;
        card.nonTaxableFees = targetOfferedSub.nonTaxableFees;

        if (!targetOfferedSub.disabled && targetOfferedSub.name === EnterpriseSubscriptionType.basic) {
          card.canSubscribe = true;
        }

        card.disabledReason = targetOfferedSub.disabledReason;
      }

      if (card.type !== SubscriptionType.Business && !profile?.referenceAddress) {
        card.disabled = true;
      }

      if (card.type === SubscriptionType.Business && !targetExpiredSub) {
        if (!card.active) {
          if (card.name === EnterpriseSubscriptionType.basic) {
            card.icon = BASICBLUE;
          } else if (card.name === EnterpriseSubscriptionType.premium) {
            card.icon = PREMIUMBLUE;
          }
        } else {
          if (card.name === EnterpriseSubscriptionType.basic) {
            card.icon = BASICWHITE;
          } else if (card.name === EnterpriseSubscriptionType.premium) {
            card.icon = PREMIUMWHITE;
          }
        }
      }
    });

    return this.checkForUpgradableSubs(filteredCards);
  }

  getSubscriptionPrice(targetOfferedSub: SubscriptionDefinitionDto): number {
    switch (targetOfferedSub.subscriptionType) {
      case SubscriptionType.Business:
        return targetOfferedSub.priceNetto;

      case SubscriptionType.Ambassador:
        return targetOfferedSub.priceNetto;

      case SubscriptionType.SceMembership:
        return targetOfferedSub.priceNetto + targetOfferedSub.nonTaxableFees;

      case SubscriptionType.Personal:
        return targetOfferedSub.priceBrutto;
    }
  }

  checkForUpgradableSubs(filteredCards: Record<string, any>[]) {
    const goldSub = filteredCards?.find(
      (item) => item.type === SubscriptionType.Personal && item.subType === PersonalSubscriptionType.Gold
    );

    const platinumSub = filteredCards?.find(
      (item) => item.type === SubscriptionType.Personal && item.subType === PersonalSubscriptionType.Platinum
    );

    if (goldSub?.active && platinumSub && !platinumSub.active) {
      platinumSub.upgradable = true;
    }

    if (platinumSub?.active && goldSub?.active) {
      platinumSub.forbidden = true;
    }

    const basicSub = filteredCards?.find(
      (item) => item.type === SubscriptionType.Business && item.name === EnterpriseSubscriptionType.basic
    );

    const premiumSub = filteredCards?.find(
      (item) => item.type === SubscriptionType.Business && item.name === EnterpriseSubscriptionType.premium
    );

    const normalizedEndsDate = +DateUtils.addTimeZoneOffset(new Date(premiumSub?.endsAt));
    const normalizedBasicEndsDate = +DateUtils.addTimeZoneOffset(new Date(basicSub?.endsAt));
    const resetedTodayDate = +DateUtils.resetDateTime(new Date());

    if (basicSub?.active && premiumSub && !premiumSub?.active) {
      basicSub.upgradable = true;
      basicSub.upgradableTo = premiumSub.id;
    }

    if (premiumSub) {
      premiumSub.basicIsActive = normalizedBasicEndsDate > resetedTodayDate;
    }

    if (normalizedBasicEndsDate < resetedTodayDate) {
      basicSub.upgradable = false;
      basicSub.active = false;
      delete basicSub.endsAt;
      delete basicSub.startedAt;
    }
    if (!basicSub?.active && premiumSub?.active && basicSub?.canSubscribe) {
      if (normalizedEndsDate < resetedTodayDate) {
        basicSub.available = true;
      }
    }
    return filteredCards;
  }

  getSubscriptionStatus(card: SubscriptionCard): SubscriptionStatus | null {
    const normalizedEndsDate = +DateUtils.addTimeZoneOffset(new Date(card.endsAt));
    const resetedTodayDate = +DateUtils.resetDateTime(new Date());

    if (normalizedEndsDate < resetedTodayDate) {
      return SubscriptionStatus.expired;
    }

    if (normalizedEndsDate - resetedTodayDate < MONTH_IN_MILISECONDS * 3) {
      return SubscriptionStatus.soonExpired;
    }

    //TODO: add condition for cancelled subscription
    if (false) {
      return SubscriptionStatus.cancelled;
    }

    if (card.active) {
      return SubscriptionStatus.subscribed;
    }

    return null;
  }

  getBusinessPremiumSubscriptionExistance(profileId: string): boolean {
    return this.getActiveSubscriptions(profileId)?.some((sub) => {
      const definition = sub.definition;
      return (
        definition.subscriptionType === SubscriptionType.Business &&
        definition.name === EnterpriseSubscriptionType.premium
      );
    });
  }

  private mergeSubscriptions(subs: SubscriptionDto[]): SubscriptionDto[] {
    const mergedSubs: SubscriptionDto[] = [];

    subs?.forEach((sub) => {
      const existingSubIndex = mergedSubs?.findIndex(
        (mergedSub) =>
          mergedSub.definition.subscriptionType === sub.definition.subscriptionType &&
          mergedSub.definition.personalSubscriptionType === sub.definition.personalSubscriptionType
      );

      if (existingSubIndex > -1) {
        if (new Date(mergedSubs[existingSubIndex].endsAt) < new Date(sub.endsAt)) {
          mergedSubs[existingSubIndex].endsAt = sub.endsAt;
        }
      } else {
        mergedSubs.push(sub);
      }
    });

    return mergedSubs;
  }
}
